<template>
  <div class="h-full flex flex-col">
    <!-- 検索に時間がかかるので、枠の真ん中に大きく出す -->
    <div v-if="loading" class="loading-box">Searching...</div>
    <div v-else-if="!loaded" class="empty-text">期間を入れて「検索」を押してください。</div>
    <div v-else-if="!groups.length" class="empty-text">
      <div>対象データがありません。</div>
      <div class="group-note">{{ note }}</div>
    </div>
    <!-- 札・表を表の幅でまとめて画面の左右中央に置く（一括変更と同じ） -->
    <div v-else class="center-col">
      <div class="group-note">{{ note }}</div>

      <!-- 集計の札 -->
      <div class="chips">
        <div class="chip"><span class="chip-label">清掃員</span><span class="chip-value">{{ groups.length }}名</span></div>
        <div class="chip"><span class="chip-label">賃金の行</span><span class="chip-value">{{ rows.length }}行</span></div>
      </div>

      <div class="table-wrap min-h-0">
        <table class="multi-table">
          <thead>
            <tr>
              <th class="text-left">清掃員</th>
              <th>清掃員コード</th>
              <th>時給</th>
              <th class="text-left">物件</th>
              <th>賃金の期間<br /><span class="sub">この期間に効いている日</span></th>
              <th>雇用指示の期間</th>
              <th class="seq">シーケンス</th>
            </tr>
          </thead>
          <!-- 清掃員ごとにまとめる（清掃員名・コードはその人の行をまたいで1つ）。違う時給と重なっている行の時給は赤 -->
          <tbody v-for="g in groups" :key="g.staffDailyCleaning" class="staff-group">
            <tr v-for="(item, i) in g.rows" :key="item.wageId" :class="item.isDiff ? 'diff' : ''">
              <td v-if="i === 0" :rowspan="g.rows.length" class="text-left staff-cell">
                <div class="staff-name">{{ g.nameStaff || '-' }}</div>
                <div class="sub">時給：{{ g.wages.map(w => yen(w)).join(' ／ ') }}</div>
              </td>
              <td v-if="i === 0" :rowspan="g.rows.length" class="staff-cell">{{ g.staffDailyCleaning }}</td>
              <td class="money big" :class="item.isDiff ? 'diff-wage' : ''">{{ yen(item.basicWage) }}</td>
              <td class="text-left">
                <div class="site-name">{{ item.siteName || item.cdSite }}</div>
                <div v-if="item.siteAddress" class="sub">{{ item.siteAddress }}</div>
                <div class="sub">契約：{{ item.cdContract || '－' }}</div>
              </td>
              <td class="whitespace-nowrap">
                {{ slash(item.fromDay) }} ～ {{ slash(item.toDay) }}
                <div class="sub">賃金の開始日 {{ slash(item.wageStartDate) }}</div>
              </td>
              <td class="sub whitespace-nowrap">{{ slash(item.startDateWork) }} ～ {{ slash(item.endDateWork) }}</td>
              <td class="seq">{{ item.sequenceNumber }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import * as moment from 'moment'

export default {
  props: {
    // 期間内の賃金の行（清掃員のカナ順 → コード → 雇用指示の時給が高い順で来る）
    rows: {
      type: Array,
      default: () => []
    },
    // 検索したときの期間
    startDate: {
      type: String,
      default: ''
    },
    endDate: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    loaded: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    /**
     * 清掃員ごとのまとまり（来た順のまま。その人の時給の種類も持つ）
     */
    groups() {
      const groups = []
      let cur = null
      for (const r of this.rows) {
        if (!cur || cur.staffDailyCleaning !== r.staffDailyCleaning) {
          cur = { staffDailyCleaning: r.staffDailyCleaning, nameStaff: r.nameStaff, rows: [], wages: [] }
          groups.push(cur)
        }
        cur.rows.push(r)
        if (r.isDiff && !cur.wages.includes(r.basicWage)) {
          cur.wages.push(r.basicWage)
        }
      }
      for (const g of groups) {
        g.wages.sort((a, b) => a - b)
      }
      return groups
    },

    /**
     * 検索した期間の説明
     */
    note() {
      return `${this.slash(this.startDate)} ～ ${this.slash(this.endDate)} の同じ日に、違う雇用指示で違う時給が効いている清掃員`
    }
  },

  methods: {
    slash(day) {
      return day ? moment(day).format('YYYY/MM/DD') : ''
    },

    yen(val) {
      if (val === null || val === undefined || val === '') {
        return '-'
      }
      return `${Number(val).toLocaleString()}円`
    }
  }
}
</script>

<style scoped>
.empty-text {
  font-size: 0.875rem;
  color: #9ca3af;
}

/* 検索中（枠の真ん中に大きく。一括変更と同じ） */
.loading-box {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: bold;
  color: #6b7280;
}

/* 検索した期間の説明（札の上。一括変更と同じ） */
.group-note {
  flex-shrink: 0;
  margin: 0 0 8px;
  font-size: 0.8rem;
  font-weight: bold;
  color: #3730a3;
}

.center-col {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-height: 0;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
}

/* 集計の札（一括変更と同じ形） */
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin: 0 0 12px;
  flex-shrink: 0;
}

.chip {
  display: inline-flex;
  align-items: stretch;
  min-width: 150px;
  border: 1px solid #c7d2fe;
  border-radius: 4px;
  overflow: hidden;
  font-size: 0.8rem;
}

.chip-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  flex-shrink: 0;
  padding: 3px 6px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.7rem;
  white-space: nowrap;
}

.chip-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 3px 10px;
  background: #fff;
  font-weight: bold;
  white-space: nowrap;
}

/* 表（一括変更と同じ作り） */
.table-wrap {
  flex: 0 1 auto;
  max-width: 100%;
  overflow: auto;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
}

.multi-table {
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8rem;
  color: #1f2937;
}

.multi-table th,
.multi-table td {
  padding: 5px 10px;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

.multi-table th:last-child,
.multi-table td:last-child {
  border-right: none;
}

/* 清掃員のまとまりの境目は濃い線 */
.multi-table .staff-group tr:last-child td {
  border-bottom: 2px solid #9ca3af;
}

.multi-table .staff-group:last-child tr:last-child td {
  border-bottom: none;
}

.multi-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #e0e7ff;
  color: #3730a3;
  font-weight: bold;
  font-size: 0.75rem;
  border-bottom-color: #c7d2fe;
}

.multi-table .text-left {
  text-align: left;
}

.multi-table .sub {
  font-size: 0.7rem;
  color: #6b7280;
}

/* 清掃員名・コードのセルは行をまたぐので、行の色・ホバーの色を付けない */
.multi-table tbody.staff-group tr td.staff-cell {
  background: #fff;
}

.multi-table .staff-name {
  font-weight: bold;
  color: #3730a3;
}

.multi-table .site-name {
  font-weight: bold;
  color: #1d4ed8;
}

.multi-table .money {
  font-weight: bold;
}

.multi-table .money.big {
  font-size: 0.95rem;
  color: #1e3a8a;
}

/* 違う時給と同じ日に重なっている行（時給を赤に） */
.multi-table .money.diff-wage {
  color: #dc2626;
}

.multi-table tr.diff td {
  background: #fff7f7;
}

/* シーケンスは手がかりなので薄く */
.multi-table .seq {
  color: #9ca3af;
  font-size: 0.7rem;
}

.multi-table tbody tr:hover td {
  background: #f8faff;
}
</style>
